import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bike, ArrowRight, CheckCircle2, Car, GraduationCap, Newspaper, BookOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import MotoShell from '../components/MotoShell';
import { MOTO_PARCOURS, MOTO_PLATEAU, MOTO_LIVRET, MOTO_STATS } from '../data/motoUnivers';

export default function MotoHomePage() {
  const { user, openRegister } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Code 2 Roues — Le code moto, façon magazine (gratuit, ECSR)';
    return () => { document.title = 'CodeVoiture.fr — Réussir le Code et la conduite (gratuit, ECSR)'; };
  }, []);

  const goDashboard = () => {
    if (user?.role === 'enseignant') navigate('/enseignant/dashboard');
    else if (user?.role === 'secretaire') navigate('/secretariat');
    else if (user?.is_admin) navigate('/admin');
    else navigate('/eleve/dashboard');
  };

  return (
    <MotoShell>
      <div data-testid="moto-home">
      {/* Hero */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-12 sm:pt-20 pb-10 sm:pb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sky-400/30 bg-sky-500/10 text-sky-300 text-[10.5px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-5">
            <Bike size={12} /> AM · A1 · A2 · Passerelle A · 125 (B) · L5e
          </div>
          <h1 className="font-serif-editorial text-[34px] sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
            Le 2 roues, notre passion.<br /><span className="text-sky-400">Ta liberté.</span>
          </h1>
          <p className="text-white/65 text-[14px] sm:text-[15.5px] mt-4 sm:mt-6 max-w-2xl leading-relaxed">
            La plateforme pédagogique moto pensée par des moniteurs agréés ECSR — désormais réunie avec
            <a href="https://codevoiture.fr" target="_blank" rel="noopener noreferrer" className="text-sky-300 font-semibold"> Code Voiture</a> :
            <strong className="text-white"> un seul compte, tous tes permis</strong>. Gratuit, sans pub, sans QCM à rallonge.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button onClick={() => (user ? goDashboard() : openRegister())} data-testid="moto-hero-cta" className="h-12 px-6 rounded-full bg-sky-500 hover:bg-sky-400 text-black text-[14px] font-bold inline-flex items-center gap-2 shadow-lg shadow-sky-900/40 transition-colors">
              Commencer mon parcours <ArrowRight size={15} />
            </button>
            <Link to="/moto/enquetes" data-testid="moto-hero-magazine" className="h-12 px-6 rounded-full border border-white/15 text-white text-[14px] font-medium hover:bg-white/5 inline-flex items-center gap-2 transition-colors">
              <Newspaper size={15} /> Lire le magazine
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
            {MOTO_STATS.map((s, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                <div className="font-serif-editorial text-sky-300 text-[24px] leading-none">{s.v}</div>
                <div className="text-white/50 text-[11px] mt-1.5 leading-snug">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Magazine + ETM */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-4 sm:mt-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <Link to="/moto/enquetes" data-testid="moto-card-magazine" className="group rounded-2xl border border-sky-500/25 bg-gradient-to-br from-[#081020] to-[#0b0f1a] p-6 sm:p-8 hover:border-sky-500/50 transition-colors">
            <Newspaper size={22} className="text-sky-400 mb-3" />
            <div className="font-serif-editorial text-[22px] sm:text-[26px] leading-tight group-hover:text-sky-200 transition-colors">31 enquêtes magazine</div>
            <p className="text-white/55 text-[13px] mt-2 leading-relaxed">Témoignages, chiffres ONISR, pièges réels — le 2 roues raconté comme il se vit, par des moniteurs ECSR.</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sky-300 text-[13px] font-semibold">Lire les enquêtes <ArrowRight size={13} /></span>
          </Link>
          <Link to="/moto/apprendre" data-testid="moto-card-etm" className="group rounded-2xl border border-white/10 bg-[#0b0f1a] p-6 sm:p-8 hover:border-sky-500/50 transition-colors">
            <BookOpen size={22} className="text-sky-400 mb-3" />
            <div className="font-serif-editorial text-[22px] sm:text-[26px] leading-tight group-hover:text-sky-200 transition-colors">Le code moto (ETM) en 9 dossiers</div>
            <p className="text-white/55 text-[13px] mt-2 leading-relaxed">Le programme officiel 2026 de l&apos;Épreuve Théorique Moto, décortiqué numéro par numéro + articles satellites.</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sky-300 text-[13px] font-semibold">Ouvrir les dossiers <ArrowRight size={13} /></span>
          </Link>
        </div>
      </section>

      {/* Parcours */}
      <section id="parcours" className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-20">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-400 font-bold font-mono-spec mb-2">Parcours de formation</div>
        <h2 className="font-serif-editorial text-[24px] sm:text-[34px] leading-tight">Trouve ton chemin vers les 2 roues.</h2>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {MOTO_PARCOURS.map((p) => (
            <div key={p.id} data-testid={`moto-parcours-${p.id}`} className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0b0f1a] hover:border-sky-500/40 transition-colors">
              <div className="h-28 sm:h-36 overflow-hidden">
                <img src={p.img} alt={p.code} loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition duration-500" />
              </div>
              <div className="p-3.5 sm:p-4">
                <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold">{p.age}</div>
                <div className="text-white font-serif-editorial text-[17px] sm:text-[19px] mt-0.5">{p.code}</div>
                <p className="text-white/55 text-[11.5px] sm:text-[12px] mt-1.5 leading-snug">{p.desc}</p>
                {p.id === 'am' && (
                  <Link to="/livret-am/lecteur" data-testid="moto-am-livret-link" className="mt-2 inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 text-[11.5px] font-bold">
                    📖 Livret Topolino · Mission 8 h (67 p.) <ArrowRight size={11} />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Plateau */}
      <section id="plateau" className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
        <div className="rounded-2xl sm:rounded-3xl border border-sky-500/20 bg-gradient-to-br from-[#081020] via-[#070b14] to-[#0b0f1a] p-5 sm:p-8 lg:p-10">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-400 font-bold font-mono-spec mb-2">Plateau interactif · Officiel 2026</div>
          <h2 className="font-serif-editorial text-[24px] sm:text-[34px] leading-tight">Les 6 manœuvres en un coup d&apos;œil</h2>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-2 max-w-2xl">Le parcours unique imposé sur tous les centres d&apos;examen français. 10 minutes pour démontrer ta maîtrise.</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MOTO_PLATEAU.map((m) => (
              <div key={m.n} data-testid={`moto-plateau-${m.n}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:border-sky-500/40 transition-colors">
                <div className="flex items-center gap-2.5">
                  <span className="font-mono-spec text-sky-400 font-bold text-[15px]">{m.n}</span>
                  <span className="text-white/40 text-[10.5px] font-mono-spec uppercase tracking-widest">{m.spec}</span>
                </div>
                <div className="text-white font-serif-editorial text-[18px] mt-1.5">{m.titre}</div>
                <div className="text-white/50 text-[12px] mt-1">{m.detail}</div>
              </div>
            ))}
          </div>
          <Link to="/moto/plateau" data-testid="moto-home-plateau-link" className="mt-5 inline-flex items-center gap-1.5 text-sky-300 hover:text-sky-200 text-[13px] font-semibold">Voir le plateau détaillé : 12 exercices + critères de réussite <ArrowRight size={13} /></Link>
        </div>
      </section>

      {/* Livret */}
      <section id="livret" className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-400 font-bold font-mono-spec mb-2">Pédagogique · REMC officiel</div>
        <h2 className="font-serif-editorial text-[24px] sm:text-[34px] leading-tight">Mon livret d&apos;apprentissage moto</h2>
        <p className="text-white/60 text-[13px] sm:text-[14px] mt-2 max-w-2xl">Le référentiel officiel des 38 compétences du permis A. Suis ta progression et auto-évalue-toi gratuitement — sauvegardé sur ton compte unique.</p>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {MOTO_LIVRET.map((c) => (
            <Link to="/moto/livret" key={c.code} data-testid={`moto-livret-${c.code}`} className="rounded-xl border border-white/10 bg-[#0b0f1a] p-4 sm:p-5 hover:border-sky-500/40 transition-colors">
              <div className="font-serif-editorial text-sky-400 text-[26px] leading-none">{c.code}</div>
              <div className="text-white font-semibold text-[14px] mt-2">{c.verbe}</div>
              <div className="text-white/60 text-[12px] mt-0.5 leading-snug">{c.titre}</div>
              <div className="text-white/35 text-[10.5px] font-mono-spec mt-2">{c.sous} sous-compétences</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Fusion : un seul compte */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-r from-[#100609] via-[#0a0a0b] to-[#081020] p-5 sm:p-8 lg:p-10">
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50 font-bold font-mono-spec mb-2">Le hub LLB ECSR</div>
              <h2 className="font-serif-editorial text-[24px] sm:text-[32px] leading-tight">Un seul compte. <span className="text-red-400">Voiture</span> + <span className="text-sky-400">moto</span>.</h2>
              <p className="text-white/65 text-[13.5px] mt-3 leading-relaxed max-w-lg">
                Code Voiture et Code 2 Roues fusionnent : ton livret, ta progression, ton planning et ta messagerie
                fonctionnent pour <strong className="text-white">tous tes permis</strong>. Ton moniteur te suit sur les deux univers.
              </p>
              <ul className="mt-4 space-y-2 text-white/80 text-[13px]">
                {['Un seul compte élève / enseignant', 'Planning et fiches de séance partagés (auto + moto)', 'Fiches flash & bilans de fin de cours pour les leçons moto aussi'].map((s, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-sky-400 shrink-0" /> {s}</li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {!user && (
                  <button onClick={openRegister} data-testid="moto-fusion-register" className="h-11 px-5 rounded-full bg-white text-black text-[13.5px] font-bold inline-flex items-center gap-2 hover:bg-white/90 transition-colors">
                    Créer mon compte unique <ArrowRight size={14} />
                  </button>
                )}
                <a href="https://codevoiture.fr" target="_blank" rel="noopener noreferrer" data-testid="moto-fusion-cv" className="h-11 px-5 rounded-full border border-red-500/40 text-red-300 hover:bg-red-950/20 text-[13.5px] font-semibold inline-flex items-center gap-2 transition-colors">
                  <Car size={14} /> Côté voiture : codevoiture.fr
                </a>
              </div>
            </div>
            <div className="hidden lg:grid grid-cols-2 gap-3" aria-hidden="true">
              <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-5">
                <Car size={20} className="text-red-400 mb-2" />
                <div className="font-serif-editorial text-[18px]">Code Voiture</div>
                <div className="text-white/50 text-[12px] mt-1">B · AAC · CS · B96 · B+E · BEA</div>
              </div>
              <div className="rounded-xl border border-sky-500/30 bg-sky-950/20 p-5">
                <Bike size={20} className="text-sky-400 mb-2" />
                <div className="font-serif-editorial text-[18px]">Code 2 Roues</div>
                <div className="text-white/50 text-[12px] mt-1">AM · A1 · A2 · A · 125 · L5e</div>
              </div>
              <div className="col-span-2 rounded-xl border border-white/10 bg-white/[0.03] p-5 text-center">
                <GraduationCap size={20} className="text-white/70 mx-auto mb-2" />
                <div className="text-white/80 text-[13px] font-semibold">1 compte · 1 moniteur · 2 univers</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </MotoShell>
  );
}
